import React, { useEffect, useState } from "react";

interface CodeLine {
  indent: number;
  text: string;
  color: string;
}

interface ArchitectureCardData {
  title: string;
  fileName: string;
  footer: string;
  badge: string;
  relation?: string;
  code: CodeLine[];
}

const architectureCards: ArchitectureCardData[] = [
  {
    title: "RexfordProfileController",
    fileName: "rexford-profile.controller.ts",
    footer: "Serves identity, experience, and proof points",
    badge: "Profile API",
    relation: "drives",
    code: [
      {
        indent: 0,
        text: "// Rexford's portfolio architecture",
        color: "#6B7280",
      },
      { indent: 0, text: '@Controller("api/profile")', color: "#F59E0B" },
      {
        indent: 0,
        text: "export class RexfordProfileController {",
        color: "#EC4899",
      },
      { indent: 1, text: "@Get()", color: "#F59E0B" },
      {
        indent: 1,
        text: "async getProfile(): Promise<ProfileSummary> {",
        color: "#3B82F6",
      },
      { indent: 2, text: "return await this.profileService", color: "#8B5CF6" },
      { indent: 3, text: ".buildSummary({ cache: true });", color: "#8B5CF6" },
      { indent: 1, text: "}", color: "#EC4899" },
      { indent: 0, text: "}", color: "#EC4899" },
    ],
  },
  {
    title: "CareerJourneyService",
    fileName: "career-journey.service.ts",
    footer: "Composes the career story behind the portfolio",
    badge: "Narrative layer",
    code: [
      { indent: 0, text: "// Profile data orchestration", color: "#6B7280" },
      { indent: 0, text: "@Injectable()", color: "#F59E0B" },
      {
        indent: 0,
        text: "export class CareerJourneyService {",
        color: "#EC4899",
      },
      {
        indent: 1,
        text: "constructor(private readonly contentStore: ContentStore) {}",
        color: "#3B82F6",
      },
      {
        indent: 1,
        text: "async buildSummary(options: BuildOptions): Promise<ProfileSummary> {",
        color: "#3B82F6",
      },
      { indent: 2, text: "return this.contentStore", color: "#8B5CF6" },
      { indent: 3, text: ".mergeCareerSignals(options);", color: "#8B5CF6" },
      { indent: 1, text: "}", color: "#EC4899" },
      { indent: 0, text: "}", color: "#EC4899" },
    ],
  },
];

interface ArchitectureCardProps {
  card: ArchitectureCardData;
  activeLine: number;
  isPrimary?: boolean;
  className?: string;
}

const ArchitectureCard: React.FC<ArchitectureCardProps> = ({
  card,
  activeLine,
  isPrimary = false,
  className = "",
}) => {
  return (
    <div
      className={`min-w-[17rem] flex-1 overflow-hidden rounded-[24px] border border-white/10 bg-white/[0.02] backdrop-blur-sm transition-all duration-300 ${
        isPrimary ? "shadow-[0_18px_50px_rgba(0,0,0,0.22)]" : ""
      } ${className}`}
    >
      <div className="flex items-start justify-between gap-4 px-4 py-4 border-b border-white/10 md:px-5">
        <div className="min-w-0 space-y-2">
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] text-text-secondary">
            {card.badge}
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold tracking-tight text-text-primary md:text-[15px]">
              {card.title}
            </h3>
            <p className="mt-1 truncate font-mono text-[11px] text-text-muted/90">
              {card.fileName}
            </p>
          </div>
        </div>
        <div className="shrink-0 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] font-mono uppercase tracking-[0.16em] text-text-muted/90">
          {card.relation ?? "linked by profile state"}
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5 text-[11px] font-mono text-text-muted/90 md:px-5">
        <span
          className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400/80"
          aria-hidden="true"
        />
        <span className="truncate">{card.footer}</span>
      </div>

      <div className="p-4 overflow-x-auto font-mono text-xs leading-relaxed md:p-5 md:text-sm">
        {card.code.map((line, index) => (
          <div
            key={`${card.title}-${line.indent}-${line.text}`}
            className={`whitespace-nowrap transition-all duration-300 ${
              index === activeLine ? "opacity-100" : "opacity-50"
            }`}
            style={{
              paddingLeft: `${line.indent * 1.2}rem`,
              color: line.color,
            }}
          >
            {index === activeLine && (
              <span className="inline-block h-3.5 w-1.5 animate-pulse bg-accent mr-1.5" />
            )}
            {line.text}
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 px-4 py-3 font-mono text-[11px] text-text-muted/90 md:px-5">
        {card.footer}
      </div>
    </div>
  );
};

const LiveCode: React.FC = () => {
  const [currentLine, setCurrentLine] = useState(0);
  const maxLines = Math.max(
    ...architectureCards.map((card) => card.code.length),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentLine((prev) => (prev + 1) % maxLines);
    }, 2000);

    return () => clearInterval(interval);
  }, [maxLines]);

  return (
    <div className="w-full mx-auto max-w-7xl">
      <div className="overflow-hidden rounded-[32px] border border-white/10 bg-gradient-to-br from-bg-elevated via-bg-elevated to-bg-primary/95 p-4 shadow-[0_22px_90px_rgba(0,0,0,0.22)] backdrop-blur-xl md:p-6 lg:p-7">
        <div className="flex items-center gap-2 pb-4 mb-5 border-b border-white/10">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-red-500/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          </div>
          <span className="ml-3 font-mono text-xs text-text-muted/90">
            rexford-profile.controller.ts · career-journey.service.ts
          </span>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-5 md:overflow-visible md:pb-2">
          {architectureCards.map((card, index) => (
            <React.Fragment key={card.title}>
              {index > 0 && (
                <div className="flex items-center justify-center gap-3 px-2 font-mono text-[11px] uppercase tracking-[0.18em] text-text-muted/80 md:hidden">
                  <span className="flex-1 h-px bg-border" />
                  <span>Dependency link</span>
                  <span className="flex-1 h-px bg-border" />
                </div>
              )}
              <ArchitectureCard
                card={card}
                activeLine={currentLine % card.code.length}
                isPrimary={index === 0}
                className="md:min-w-0 md:flex-[1_1_0]"
              />
            </React.Fragment>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 mt-5 font-mono text-xs border-t border-white/10 text-text-muted/90">
          <span>TypeScript • NestJS • Personal brand</span>
          <span className="items-center hidden gap-2 md:flex">
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400"
              aria-hidden="true"
            />
            <span>Building a portfolio that feels like my stack.</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default LiveCode;
